/**
 * Turn-index tracker — single source of truth for the server's authoritative
 * `turnIndex` in client-side analytics.
 *
 * The server sets `messageMetadata.turnIndex` on the first chat response
 * frame (see `lib/server/http/chat.ts`). `ChatPanel` calls
 * `setLatestServerTurnIndex` as those values arrive. Cold-start beacons
 * (page mount before any chat request) stay at the -1 sentinel and don't
 * read this ref — that's correct, since they predate any server turn. Latency beacons (`time_to_first_chip`,
 * `user_to_first_widget_ms`) read this ref so they tag with the same
 * `(sessionId, turnIndex)` namespace as server-emitted metrics
 * (`model_ttft`, `ttft`, `chat_total`, …) — which fixes the Debug Panel "Turn"
 * tab and makes downstream eval / Datadog / Amplitude joins work.
 *
 * Module scope: this is a host-process concern (one debug panel, one
 * AnalyticsContext per app), not a transport concern — it lives in
 * `@health/sdk-react`, never in `@health/sdk-client`. See CLAUDE.md
 * "SDK Boundary Discipline".
 *
 * Sentinel: `-1` means "pre-turn / unknown" and matches the existing
 * convention at `lib/analytics/emitter.ts:436` (`opts.turnIndex ?? -1`).
 * Cold-start beacons (page mount before any server response) emit at -1 —
 * correct semantically, since they predate any server turn.
 *
 * Plan: docs/plans/done/2026-04-25-unified-turn-index-source-of-truth.md
 */

import { stampLatestTurnIndex } from "./useTurnTiming";

let latestServerTurnIndex = -1;

/**
 * Update the latest known server turnIndex. Called by `ChatPanel` from:
 * - the chat stream consumer when a UIMessage arrives with
 *   `metadata.turnIndex` (chat endpoint)
 * - the `getGreeting` response handler reading `body.turnIndex`
 *
 * Validates: must be a non-negative integer. Invalid values (NaN, negative,
 * non-integer) are ignored — the previous value is retained. This protects
 * the tracker from rolling back to a sentinel on a malformed / pre-turn
 * frame (e.g. cold-start metadata that hasn't been populated server-side).
 *
 * Side effect: stamps the still-pending turn-timing registry entry so the
 * captured `turnIndexAtStart` reflects the authoritative server value before
 * the widget-render beacon fires.
 */
export function setLatestServerTurnIndex(value: number | null | undefined): void {
  if (typeof value !== "number") return;
  if (!Number.isInteger(value) || value < 0) return;
  latestServerTurnIndex = value;
  stampLatestTurnIndex(value);
}

/** Returns the latest known server turnIndex, or -1 if none has been seen. */
export function getLatestServerTurnIndex(): number {
  return latestServerTurnIndex;
}

/**
 * Reset to the initial state. Called by `ChatPanel.resetSession()` on "clear
 * conversation" / site-switch / category-switch — without this, the previous
 * session's tail turnIndex would attribute the new session's cold-start beacon
 * to the wrong turn.
 */
export function resetLatestServerTurnIndex(): void {
  latestServerTurnIndex = -1;
}

/** Test-only alias. */
export const _resetLatestServerTurnIndexForTests = resetLatestServerTurnIndex;
